/**
 * The bill table's number cells (DESIGN.md §12 "3PL usage bill", §14): one
 * value per cell, right-aligned by the caller, tabular and unbroken, and a
 * muted "-" for nothing or zero, never a blank cell.
 *
 * Promoted from `features/reporting/components/ThreePlCells.tsx` when a second
 * feature (Settings > Billing) started drawing a bill: Reports and Billing now
 * share one cell, so a bill reads the same everywhere. The caller owns padding
 * and alignment (`className`) and the money formatter (`formatValue`), so the
 * cell never picks a currency itself.
 */
import { formatCountCell } from '@/lib/format-count';
import { cn } from '@/lib/utils';

interface CountCellProps {
  value: number;
  /** Bold a non-zero value. */
  strong?: boolean;
  /** A table cell, or a span inside a cell that holds more than the count. */
  as?: 'td' | 'span';
  /** Padding and alignment of a `td`. */
  className?: string;
}

/** One count per cell, tabular and unbroken; a zero is a muted "-". */
export function CountCell({ value, strong = false, as: Tag = 'td', className }: CountCellProps) {
  return (
    <Tag
      className={cn(
        'whitespace-nowrap',
        className,
        'tabular-nums',
        value === 0 ? 'text-muted-foreground' : strong && 'font-semibold',
      )}
    >
      {formatCountCell(value)}
    </Tag>
  );
}

interface MoneyCellProps {
  /** Null = nothing to show. */
  value: number | null;
  /** Formats in the bill's own currency, never the viewer's. */
  formatValue: (value: number) => string;
  /** Bold a shown value. */
  strong?: boolean;
  /** Show a zero as money: a price in force that is deliberately free. */
  showZero?: boolean;
  as?: 'td' | 'span';
  className?: string;
}

/** One amount per cell, tabular and unbroken; nothing or a zero is a muted "-". */
export function MoneyCell({
  value,
  formatValue,
  strong = false,
  showZero = false,
  as: Tag = 'td',
  className,
}: MoneyCellProps) {
  const shown = value === null || (value === 0 && !showZero) ? null : formatValue(value);
  return (
    <Tag
      className={cn(
        'whitespace-nowrap',
        className,
        'tabular-nums',
        shown === null ? 'text-muted-foreground' : strong && 'font-semibold',
      )}
    >
      {shown ?? formatCountCell(0)}
    </Tag>
  );
}
